import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { UtilService } from 'src/app/services/util.service';
import { UseraccessserviceService } from 'src/app/services/useraccessservice.service';
import { OfflineDbService } from 'src/app/services/offline/offline-db.service';
import { OfflineSaveService } from 'src/app/services/offline/offline-save.service';
import { OfflineEntitlementService, EnrolledModuleSummary } from 'src/app/services/offline/offline-entitlement.service';
import { OfflineContentReaderService } from 'src/app/services/offline/offline-content-reader.service';
import { OfflineMediaService } from 'src/app/services/offline/offline-media.service';
import { OfflineBootstrapCacheService } from 'src/app/services/offline/offline-bootstrap-cache.service';
import { IndexRecord } from 'src/app/services/offline/offline-db.types';
import { getCurrentSchoolSubdomain, getCurrentUserId } from 'src/app/services/offline/offline-cache-key';
import { estimateQuota, QuotaEstimate, requestPersistence } from 'src/app/services/offline/offline-quota';
import { collectOfflineDiagnostics, OfflineDiagnostics, summariseDiagnostics } from 'src/app/services/offline/offline-diagnostics';

const ENROLLED_MODULES_CACHE_KIND = 'enrolledmodules';

interface DownloadLessonNode {
  classId: string;
  title: string;
  saved: boolean;
  sizeBytes: number;
  savedAt: number;
}

interface DownloadCourseNode {
  moduleId: number;
  title: string;
  expandable: boolean;
  lessons: DownloadLessonNode[];
  // Saved videos in this course that the server has since reprocessed. Counted per course,
  // not per lesson: a video is keyed to its course module, and nothing associates it with a
  // particular lesson (see offline-lesson-video-refs.ts).
  staleVideoCount: number;
}

// The downloads hub — offline access is confined entirely to this page and its lesson/note
// reader (downloadedlesson.component.ts) now; there is no more page-level caching elsewhere
// in the app to browse to instead. Reachable regardless of whether the feature flag is on,
// matching the app's other settings-style pages — it simply shows an empty list with the
// flag off, since nothing will ever have been saved in that case.
@Component({
  selector: 'app-managedownloads',
  templateUrl: './managedownloads.component.html',
  styleUrls: ['./managedownloads.component.css'],
})
export class ManagedownloadsComponent implements OnInit {
  courses: DownloadCourseNode[] = [];
  expandedModuleIds = new Set<number>();
  quota: QuotaEstimate | null = null;
  loading = true;
  wipingAll = false;
  removingClassId: string | null = null;
  persisted: boolean | null = null;

  // Collapsed by default — this is for when something is wrong, not everyday furniture.
  diagnostics: OfflineDiagnostics | null = null;
  diagnosticsSummary = '';
  showDiagnostics = false;
  diagnosticsCopied = false;

  constructor(
    private util: UtilService,
    private useraccessservice: UseraccessserviceService,
    private offlineDb: OfflineDbService,
    private offlineSave: OfflineSaveService,
    private offlineEntitlement: OfflineEntitlementService,
    private contentReader: OfflineContentReaderService,
    private offlineMedia: OfflineMediaService,
    private bootstrapCache: OfflineBootstrapCacheService,
    private toastr: ToastrService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.refresh();
    await this.refreshDiagnostics();
  }

  async refreshDiagnostics(): Promise<void> {
    this.diagnostics = await collectOfflineDiagnostics(this.offlineDb, this.useraccessservice.getCurrentUserToken());
    this.diagnosticsSummary = summariseDiagnostics(this.diagnostics);
  }

  async toggleDiagnostics(): Promise<void> {
    this.showDiagnostics = !this.showDiagnostics;
    if (this.showDiagnostics) await this.refreshDiagnostics();
  }

  get diagnosticsText(): string {
    return this.diagnostics ? JSON.stringify(this.diagnostics, null, 2) : '';
  }

  // A phone has no console to read this out of, and retyping it from a screenshot is how
  // transcription errors get introduced into a diagnosis.
  async copyDiagnostics(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.diagnosticsText);
      this.diagnosticsCopied = true;
      setTimeout(() => (this.diagnosticsCopied = false), 2000);
    } catch (err) {
      console.error('[offline] could not copy diagnostics', err);
      this.toastr.info('Could not copy automatically — select the text below and copy it.');
    }
  }

  async refresh(): Promise<void> {
    this.loading = true;
    try {
      const school = getCurrentSchoolSubdomain();
      const user = getCurrentUserId(this.useraccessservice.getCurrentUserToken());
      this.courses = school && user ? await this.buildCourseTree(school, user) : [];
      if (school && user) await this.markStaleVideos(school, user);
      this.quota = await estimateQuota();
    } finally {
      this.loading = false;
    }
  }

  // Best effort and non-blocking for correctness: offline, every lookup fails and nothing is
  // flagged, which is right - we cannot know a video is out of date without asking.
  private async markStaleVideos(school: string, user: number): Promise<void> {
    try {
      const stale = await this.offlineMedia.findStaleSavedVideos(school, user);
      for (const item of stale) {
        const course = this.courses.find((c) => c.moduleId === item.moduleid);
        if (course) course.staleVideoCount++;
      }
    } catch (err) {
      console.error('[offline] could not check saved videos for updates', err);
    }
  }

  private async buildCourseTree(school: string, user: number): Promise<DownloadCourseNode[]> {
    const entries = await this.offlineDb.listIndexEntries(school, user);
    const byModule = new Map<number, IndexRecord[]>();
    for (const entry of entries) {
      const list = byModule.get(entry.moduleId) ?? [];
      list.push(entry);
      byModule.set(entry.moduleId, list);
    }

    // Union of two sources, not just one: the cached enrolled-modules list (every enrolled
    // course, best-effort — can be empty/stale on a very first offline login) plus every
    // moduleId that actually has something saved. This guarantees a student never loses
    // visibility into content they've genuinely saved, even when that convenience cache
    // hasn't run yet.
    const enrolledSummaries = (await this.bootstrapCache.getCached<EnrolledModuleSummary[]>(ENROLLED_MODULES_CACHE_KIND)) ?? [];
    const moduleIds = new Set<number>(enrolledSummaries.map((m) => m.id));
    for (const moduleId of byModule.keys()) moduleIds.add(moduleId);

    const courses: DownloadCourseNode[] = [];
    for (const moduleId of moduleIds) {
      const moduleEntries = byModule.get(moduleId) ?? [];
      const enrolledSummary = enrolledSummaries.find((m) => m.id === moduleId);
      const navshellSaved = moduleEntries.some((e) => e.kind === 'navshell-moduleview' && e.status === 'saved');

      if (!navshellSaved) {
        // Enrolled, but nothing saved from this course yet — greyed, no expand affordance,
        // nothing to build a lesson list from.
        courses.push({ moduleId, title: enrolledSummary?.courseTitle ?? `Course ${moduleId}`, expandable: false, lessons: [], staleVideoCount: 0 });
        continue;
      }

      const navshell = await this.contentReader.read<any>(school, user, 'navshell-moduleview', String(moduleId));
      const savedLessonEntries = moduleEntries.filter((e) => e.kind === 'lesson' && e.status === 'saved');
      const coveredClassIds = new Set<string>();
      const lessons: DownloadLessonNode[] = [];

      for (const lesson of navshell?.lessons ?? []) {
        const classId = String(lesson.id);
        coveredClassIds.add(classId);
        const entry = savedLessonEntries.find((e) => e.resourceId === classId);
        lessons.push({ classId, title: lesson.title, saved: !!entry, sizeBytes: entry?.sizeBytes ?? 0, savedAt: entry?.savedAt ?? 0 });
      }
      // Orphan pass — evictLru evicts oldest-savedAt-first per unit, not per course, so a
      // course's navshell-moduleview can be evicted while a lesson under it survives. Without
      // this, that lesson would be unreachable in the hub while still consuming storage.
      for (const entry of savedLessonEntries) {
        if (coveredClassIds.has(entry.resourceId)) continue;
        const lessonContent = await this.contentReader.read<any>(school, user, 'lesson', entry.resourceId);
        lessons.push({
          classId: entry.resourceId,
          title: lessonContent?.title ?? `Lesson ${entry.resourceId}`,
          saved: true,
          sizeBytes: entry.sizeBytes,
          savedAt: entry.savedAt,
        });
      }

      courses.push({
        moduleId,
        title: navshell?.moduleinfo?.name ?? enrolledSummary?.courseTitle ?? `Course ${moduleId}`,
        expandable: true,
        lessons,
        staleVideoCount: 0,
      });
    }

    return courses.sort((a, b) => a.title.localeCompare(b.title));
  }

  toggleExpand(moduleId: number): void {
    if (this.expandedModuleIds.has(moduleId)) this.expandedModuleIds.delete(moduleId);
    else this.expandedModuleIds.add(moduleId);
  }

  isExpanded(moduleId: number): boolean {
    return this.expandedModuleIds.has(moduleId);
  }

  formatBytes(bytes: number): string {
    if (!bytes) return '0 MB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  async removeLesson(classId: string): Promise<void> {
    const confirmed = await this.util.confirm('Remove download?', 'Remove this lesson from offline storage?');
    if (!confirmed) return;
    this.removingClassId = classId;
    try {
      await this.offlineSave.unsaveLesson(classId);
    } finally {
      this.removingClassId = null;
      await this.refresh();
    }
  }

  async removeAll(): Promise<void> {
    const confirmed = await this.util.confirm('Remove all downloads?', 'This removes everything saved for offline use on this device. You can save it again anytime while online.');
    if (!confirmed) return;
    this.wipingAll = true;
    try {
      await this.offlineDb.wipeAll();
      await this.offlineEntitlement.refresh();
      this.toastr.success('All offline downloads removed.');
    } finally {
      this.wipingAll = false;
      await this.refresh();
    }
  }

  async requestPersist(): Promise<void> {
    this.persisted = await requestPersistence();
    this.toastr[this.persisted ? 'success' : 'warning'](
      this.persisted ? 'Storage persistence granted — the browser will avoid clearing this data under pressure.' : 'The browser did not grant persistent storage.',
    );
  }
}
